import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import Footer from '@/components/feature/Footer';
import Navbar from '@/components/feature/Navbar';
import { createMemberOrder, fetchShopProducts, type MemberOrderItem, type PublicProductItem } from '@/features/public/api';
import { readStoredMemberUser, withMemberSession } from '@/features/public/memberSession';
import { useRealtimeInvalidation } from '@/shared/realtime/useRealtimeInvalidation';

type CartItem = PublicProductItem & {
  quantity: number;
};

const DEFAULT_PRODUCT_IMAGE =
  "data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 640 640'><rect width='640' height='640' fill='%23FFF7F2'/><rect x='48' y='48' width='544' height='544' rx='36' fill='%23FFE9DE'/><path d='M184 420l90-104 76 66 62-52 104 90' fill='none' stroke='%23E8521A' stroke-width='28' stroke-linecap='round' stroke-linejoin='round'/><circle cx='232' cy='214' r='52' fill='%23E8521A' fill-opacity='0.18'/></svg>";

const CATEGORY_META = [
  { id: 'all', name: 'Tous', icon: 'ri-apps-line' },
  { id: 'vetements', name: 'Vêtements', icon: 'ri-t-shirt-line' },
  { id: 'accessoires', name: 'Accessoires', icon: 'ri-shopping-bag-line' },
  { id: 'sport', name: 'Sport', icon: 'ri-basketball-line' },
  { id: 'papeterie', name: 'Papeterie', icon: 'ri-book-line' },
  { id: 'electromenager', name: 'Électroménager', icon: 'ri-home-gear-line' }
] as const;

function normalizeCategoryKey(value: string) {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
}

function formatCategoryLabel(value: string) {
  return value
    .split(/[_-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function resolveCategoryMeta(category: string) {
  const normalized = normalizeCategoryKey(category);
  const existing = CATEGORY_META.find((entry) => entry.id === normalized);

  if (existing) {
    return {
      id: normalized,
      name: existing.name,
      icon: existing.icon
    };
  }

  return {
    id: normalized,
    name: formatCategoryLabel(category),
    icon: 'ri-price-tag-3-line'
  };
}

function formatMoney(value: number) {
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'XOF',
    maximumFractionDigits: 0
  }).format(value);
}

function ProductCard({
  product,
  quantity,
  onChangeQuantity,
  onAddToCart
}: {
  product: PublicProductItem;
  quantity: number;
  onChangeQuantity: (productId: string, quantity: number) => void;
  onAddToCart: (product: PublicProductItem, quantity: number) => void;
}) {
  return (
    <div className="group overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-xl">
      <div className="relative aspect-square overflow-hidden bg-gray-100">
        <img
          src={product.image ?? DEFAULT_PRODUCT_IMAGE}
          alt={product.name}
          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
        {product.badge ? (
          <span className="absolute left-3 top-3 rounded-full bg-[#E8521A] px-3 py-1 text-xs font-semibold text-white">
            {product.badge}
          </span>
        ) : null}
        {product.stock === 0 ? (
          <span className="absolute right-3 top-3 rounded-full bg-red-500 px-3 py-1 text-xs font-semibold text-white">
            Rupture
          </span>
        ) : product.stock <= 5 ? (
          <span className="absolute right-3 top-3 rounded-full bg-amber-500 px-3 py-1 text-xs font-semibold text-white">
            Stock limité
          </span>
        ) : null}
      </div>
      <div className="space-y-4 p-5">
        <div>
          <span className="text-xs uppercase tracking-wide text-gray-500">{formatCategoryLabel(product.category)}</span>
          <h3 className="mt-1 text-lg font-bold text-gray-900 transition-colors group-hover:text-orange-600">{product.name}</h3>
          <p className="mt-2 line-clamp-2 text-sm text-gray-600">{product.description}</p>
        </div>
        <div className="space-y-3">
          <div className="flex items-center justify-between gap-4">
            <div className="min-w-0">
              <p className="break-words text-xl font-bold text-[#E8521A]">{formatMoney(product.price)}</p>
              <p className="mt-1 text-xs text-gray-500">{product.stock} en stock</p>
            </div>
            <div className="inline-flex items-center gap-2 rounded-2xl border border-orange-100 bg-[#FFF7F2] px-2 py-2">
              <button
                type="button"
                onClick={() => onChangeQuantity(product.id, quantity - 1)}
                disabled={quantity <= 1}
                className="flex h-9 w-9 items-center justify-center rounded-xl bg-orange-500 text-white transition-colors hover:bg-[#d44a16] disabled:cursor-not-allowed disabled:bg-orange-200 disabled:text-white/80"
                title="Réduire la quantité"
              >
                <i className="ri-subtract-line text-lg"></i>
              </button>
              <span className="min-w-8 text-center text-sm font-semibold text-[#1A1A1A]">{quantity}</span>
              <button
                type="button"
                onClick={() => onChangeQuantity(product.id, quantity + 1)}
                disabled={quantity >= product.stock}
                className="flex h-9 w-9 items-center justify-center rounded-xl bg-orange-500 text-white transition-colors hover:bg-[#d44a16] disabled:cursor-not-allowed disabled:bg-orange-200 disabled:text-white/80"
                title="Augmenter la quantité"
              >
                <i className="ri-add-line text-lg"></i>
              </button>
            </div>
          </div>
          <button
            onClick={() => onAddToCart(product, quantity)}
            disabled={product.stock === 0}
            className={`inline-flex w-full items-center justify-center gap-2 rounded-2xl px-4 py-3 font-semibold transition-colors ${
              product.stock === 0
                ? 'cursor-not-allowed bg-gray-100 text-gray-400'
                : 'bg-[#E8521A] text-white hover:bg-[#d44a16]'
            }`}
            title={product.stock === 0 ? 'Produit indisponible' : 'Ajouter au panier'}
          >
            <i className="ri-shopping-cart-line text-lg"></i>
            Ajouter au panier
          </button>
        </div>
      </div>
    </div>
  );
}

function CartSidebar({
  isOpen,
  onClose,
  cart,
  onRemove,
  onCheckout,
  checkoutLoading,
  checkoutError
}: {
  isOpen: boolean;
  onClose: () => void;
  cart: CartItem[];
  onRemove: (id: string) => void;
  onCheckout: () => void;
  checkoutLoading: boolean;
  checkoutError: string | null;
}) {
  const total = cart.reduce((sum, item) => sum + item.price * item.quantity, 0);

  return (
    <>
      {isOpen ? <div className="fixed inset-0 z-50 bg-black/50" onClick={onClose}></div> : null}

      <div
        className={`fixed right-0 top-0 z-50 flex h-full w-full max-w-md transform flex-col bg-white shadow-2xl transition-transform duration-300 ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="flex items-center justify-between border-b border-gray-100 px-6 py-5">
          <h2 className="flex items-center gap-2 text-xl font-bold text-gray-900">
            <i className="ri-shopping-cart-line text-[#E8521A]"></i>
            Votre panier
            <span className="rounded-full bg-[#E8521A] px-2 py-0.5 text-sm text-white">{cart.length}</span>
          </h2>
          <button onClick={onClose} className="rounded-lg p-2 transition-colors hover:bg-gray-100">
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {cart.length === 0 ? (
            <div className="py-12 text-center">
              <div className="mx-auto mb-4 flex h-20 w-20 items-center justify-center rounded-full bg-gray-100">
                <i className="ri-shopping-cart-line text-3xl text-gray-400"></i>
              </div>
              <p className="text-gray-500">Votre panier est vide</p>
            </div>
          ) : (
            <div className="space-y-4">
              {cart.map((item) => (
                <div key={item.id} className="flex gap-4 rounded-2xl bg-gray-50 p-4">
                  <img src={item.image ?? DEFAULT_PRODUCT_IMAGE} alt={item.name} className="h-20 w-20 rounded-xl object-cover" />
                  <div className="min-w-0 flex-1">
                    <h4 className="truncate text-sm font-semibold text-gray-900">{item.name}</h4>
                    <p className="mt-1 text-sm font-bold text-[#E8521A]">{formatMoney(item.price)}</p>
                    <div className="mt-3 text-sm text-gray-500">Quantité sélectionnée : <span className="font-semibold text-gray-900">{item.quantity}</span></div>
                  </div>
                  <button onClick={() => onRemove(item.id)} className="p-2 text-gray-400 transition-colors hover:text-red-500">
                    <i className="ri-delete-bin-line"></i>
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {cart.length > 0 ? (
          <div className="space-y-4 border-t border-gray-100 p-6">
            <div className="flex items-center justify-between text-lg">
              <span className="font-semibold text-gray-900">Total</span>
              <span className="text-xl font-bold text-[#E8521A]">{formatMoney(total)}</span>
            </div>
            {checkoutError ? (
              <div className="rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-600">{checkoutError}</div>
            ) : null}
            <button
              onClick={onCheckout}
              disabled={checkoutLoading}
              className="w-full rounded-2xl bg-[#E8521A] py-4 font-semibold text-white transition-colors hover:bg-[#d44a16] disabled:cursor-not-allowed disabled:opacity-70"
            >
              {checkoutLoading ? 'Paiement en cours...' : 'Passer la commande'}
            </button>
            <p className="text-center text-xs text-gray-500">
              Paiement simulé sécurisé. Vous pourrez brancher votre vraie API de paiement ensuite sans refaire le tunnel de commande.
            </p>
          </div>
        ) : null}
      </div>
    </>
  );
}

export default function ShopPage() {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [cart, setCart] = useState<CartItem[]>([]);
  const [selectedQuantities, setSelectedQuantities] = useState<Record<string, number>>({});
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [products, setProducts] = useState<PublicProductItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);
  const [checkoutSuccess, setCheckoutSuccess] = useState<Pick<MemberOrderItem, 'code' | 'paymentReference'> | null>(null);
  const [cartToast, setCartToast] = useState<string | null>(null);
  const navigate = useNavigate();

  const loadProducts = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const data = await fetchShopProducts({ limit: 100 });
      setProducts(data.items);
    } catch (caughtError) {
      setError(caughtError instanceof Error ? caughtError.message : 'Impossible de charger la boutique pour le moment.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadProducts();
  }, [loadProducts]);

  useRealtimeInvalidation({
    topics: ['products'],
    requiresAuth: false,
    onInvalidate: () => {
      void loadProducts();
    }
  });

  useEffect(() => {
    const savedCart = localStorage.getItem('asc-cart');
    if (savedCart) {
      try {
        setCart(JSON.parse(savedCart) as CartItem[]);
      } catch {
        setCart([]);
      }
    }
  }, []);

  useEffect(() => {
    localStorage.setItem('asc-cart', JSON.stringify(cart));
  }, [cart]);

  useEffect(() => {
    if (!products.length) {
      return;
    }

    setSelectedQuantities((current) => {
      const next: Record<string, number> = {};

      products.forEach((product) => {
        const currentValue = current[product.id] ?? 1;
        next[product.id] = Math.min(Math.max(currentValue, 1), Math.max(product.stock, 1));
      });

      return next;
    });

    setCart((current) =>
      current
        .map((item) => {
          const latest = products.find((product) => product.id === item.id);
          if (!latest) {
            return null;
          }

          const nextQuantity = Math.min(item.quantity, Math.max(latest.stock, 0));

          if (nextQuantity < 1) {
            return null;
          }

          return {
            ...latest,
            quantity: nextQuantity
          };
        })
        .filter((item): item is CartItem => Boolean(item))
    );
  }, [products]);

  const dynamicCategories = useMemo(() => {
    const categories = new Map<string, { id: string; name: string; icon: string }>();
    categories.set('all', CATEGORY_META[0]);

    products.forEach((product) => {
      const meta = resolveCategoryMeta(product.category);
      categories.set(meta.id, meta);
    });

    return Array.from(categories.values());
  }, [products]);

  useEffect(() => {
    if (!dynamicCategories.some((category) => category.id === selectedCategory)) {
      setSelectedCategory("all");
    }
  }, [dynamicCategories, selectedCategory]);

  const filteredProducts = useMemo(() => {
    if (selectedCategory === 'all') {
      return products;
    }

    return products.filter((product) => normalizeCategoryKey(product.category) === selectedCategory);
  }, [products, selectedCategory]);

  const handleChangeSelectedQuantity = (productId: string, quantity: number) => {
    const product = products.find((item) => item.id === productId);
    if (!product) {
      return;
    }

    setSelectedQuantities((current) => ({
      ...current,
      [productId]: Math.min(Math.max(quantity, 1), Math.max(product.stock, 1))
    }));
  };

  const addToCart = (product: PublicProductItem, quantity: number) => {
    setCheckoutSuccess(null);
    setCart((current) => {
      const existing = current.find((item) => item.id === product.id);

      if (existing) {
        return current.map((item) =>
          item.id === product.id
            ? { ...item, quantity: Math.min(item.quantity + quantity, product.stock) }
            : item
        );
      }

      return [...current, { ...product, quantity }];
    });
    setCartToast(`${product.name} ajouté au panier.`);
  };

  const removeFromCart = (id: string) => {
    setCheckoutSuccess(null);
    setCart((current) => current.filter((item) => item.id !== id));
  };

  useEffect(() => {
    if (!cartToast) {
      return;
    }

    const timeout = window.setTimeout(() => setCartToast(null), 2600);
    return () => window.clearTimeout(timeout);
  }, [cartToast]);

  const handleCheckout = async () => {
    if (!cart.length) {
      return;
    }

    const currentUser = readStoredMemberUser();
    if (!currentUser) {
      navigate('/formulaires?tab=login&redirect=%2Fboutique');
      return;
    }

    setCheckoutLoading(true);
    setCheckoutError(null);
    setCheckoutSuccess(null);

    try {
      const payload = await withMemberSession((token) =>
        createMemberOrder(token, {
          memberName: [currentUser.prenom, currentUser.nom].filter(Boolean).join(' ').trim() || 'Membre ASC',
          memberEmail: currentUser.email,
          items: cart.map((item) => ({
            productId: item.id,
            quantity: item.quantity
          }))
        })
      );

      if (!payload) {
        navigate('/formulaires?tab=login&redirect=%2Fboutique');
        return;
      }

      setCheckoutSuccess({
        code: payload.code,
        paymentReference: payload.paymentReference
      });
      setCart([]);
      setIsCartOpen(false);
      await loadProducts();
    } catch (caughtError) {
      setCheckoutError(
        caughtError instanceof Error
          ? caughtError.message
          : 'Le paiement simulé n’a pas pu être validé pour le moment.'
      );
    } finally {
      setCheckoutLoading(false);
    }
  };

  const cartCount = cart.reduce((sum, item) => sum + item.quantity, 0);

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      <section className="relative overflow-hidden py-12">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=african%20sports%20merchandise%20store%20colorful%20tshirts%20caps%20bags%20accessories%20display%20vibrant%20orange%20teal%20branding%20senegal&width=1600&height=300&seq=shop-hero1&orientation=landscape"
            alt="Boutique ASC"
            className="h-full w-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-[#E8521A]/85"></div>
        </div>
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col items-start justify-between gap-6 md:flex-row md:items-center">
            <div className="text-white">
              <h1 className="text-3xl font-bold md:text-4xl">Boutique ASC</h1>
              <p className="mt-2 opacity-90">Commandez les produits du club et suivez vos achats depuis votre espace membre.</p>
            </div>
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        <div className="pointer-events-none fixed right-4 top-24 z-40 sm:right-6 lg:right-10">
          <button
            onClick={() => setIsCartOpen(true)}
            className="pointer-events-auto flex items-center gap-3 rounded-2xl bg-[#E8521A] px-5 py-3 font-semibold text-white shadow-[0_18px_35px_rgba(232,82,26,0.3)] transition-all hover:bg-[#d44a16] hover:shadow-[0_22px_42px_rgba(232,82,26,0.35)]"
          >
            <i className="ri-shopping-cart-line text-xl"></i>
            <span>Panier</span>
            <span className="flex h-7 min-w-7 items-center justify-center rounded-full bg-white px-2 text-sm font-bold text-[#E8521A]">
              {cartCount}
            </span>
          </button>
        </div>

        {cartToast ? (
          <div className="fixed right-4 top-24 z-50 rounded-2xl border border-orange-100 bg-white px-5 py-4 shadow-[0_20px_60px_rgba(232,82,26,0.18)] sm:right-6">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-orange-100 text-[#E8521A]">
                <i className="ri-shopping-cart-2-line text-lg"></i>
              </div>
              <div>
                <p className="text-sm font-semibold text-[#1A1A1A]">Produit ajouté au panier</p>
                <p className="text-sm text-gray-500">{cartToast}</p>
              </div>
            </div>
          </div>
        ) : null}

        {checkoutSuccess ? (
          <div className="mb-8 rounded-2xl border border-emerald-100 bg-emerald-50 px-5 py-4 text-sm text-emerald-800">
            <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <div>
                <p className="font-semibold">Commande {checkoutSuccess.code} enregistrée avec succès.</p>
                <p className="mt-1 text-emerald-700">
                  {checkoutSuccess.paymentReference
                    ? `Référence de paiement simulé : ${checkoutSuccess.paymentReference}.`
                    : 'Paiement simulé validé.'}
                </p>
              </div>
              <button
                onClick={() => navigate('/membre?tab=orders')}
                className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-white px-4 py-2 font-medium text-emerald-700 transition-colors hover:bg-emerald-100"
              >
                <i className="ri-external-link-line"></i>
                Suivre ma commande
              </button>
            </div>
          </div>
        ) : null}

        <div className="mb-8 flex flex-wrap gap-3">
          {dynamicCategories.map((category) => (
            <button
              key={category.id}
              onClick={() => setSelectedCategory(category.id)}
              className={`flex items-center gap-2 rounded-full px-5 py-3 font-medium transition-all ${
                selectedCategory === category.id
                  ? 'bg-[#E8521A] text-white shadow-lg shadow-orange-500/30'
                  : 'border border-gray-200 bg-white text-gray-700 hover:bg-gray-100'
              }`}
            >
              <i className={category.icon}></i>
              {category.name}
            </button>
          ))}
        </div>

        {error ? (
          <div className="rounded-2xl border border-red-100 bg-red-50 px-5 py-4 text-sm text-red-600">{error}</div>
        ) : null}

        {loading ? (
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {Array.from({ length: 8 }).map((_, index) => (
              <div key={index} className="overflow-hidden rounded-2xl border border-gray-100 bg-white">
                <div className="aspect-square animate-pulse bg-gray-100"></div>
                <div className="space-y-3 p-5">
                  <div className="h-3 w-20 animate-pulse rounded bg-gray-100"></div>
                  <div className="h-5 w-3/4 animate-pulse rounded bg-gray-100"></div>
                  <div className="h-14 animate-pulse rounded bg-gray-100"></div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {filteredProducts.map((product) => (
                <ProductCard
                  key={product.id}
                  product={product}
                  quantity={selectedQuantities[product.id] ?? 1}
                  onChangeQuantity={handleChangeSelectedQuantity}
                  onAddToCart={addToCart}
                />
              ))}
            </div>

            {filteredProducts.length === 0 ? (
              <div className="py-16 text-center">
                <div className="mx-auto mb-4 flex h-20 w-20 items-center justify-center rounded-full bg-gray-100">
                  <i className="ri-shopping-bag-line text-3xl text-gray-400"></i>
                </div>
                <p className="text-gray-500">Aucun produit disponible dans cette catégorie pour le moment.</p>
              </div>
            ) : null}
          </>
        )}
      </div>

      <CartSidebar
        isOpen={isCartOpen}
        onClose={() => setIsCartOpen(false)}
        cart={cart}
        onRemove={removeFromCart}
        onCheckout={() => void handleCheckout()}
        checkoutLoading={checkoutLoading}
        checkoutError={checkoutError}
      />
      <Footer />
    </div>
  );
}
